import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { cn } from 'lib/utils';
import AppContext from 'contexts/AppContext';
import { hasUserPermission } from 'utils/permissionUtils';
import { ActorCell } from 'components/admin/audit/ActorCell';
import ActionPill from 'components/admin/audit/ActionPill';
import { dayKey, entityLink, formatTime, numericDate, summary } from 'utils/auditLabels';
import type { AdminAuditEvent } from 'api/adminAuditApi';
import { GRID_COLS, MUTED_CELL, moduleLabel, type OpenEvent } from './tableColumns';

interface EventRowProps {
  event: AdminAuditEvent;
  onOpen: OpenEvent;
  /** Child row of an import (design §6.4): indented, 44px, time only, no date. */
  dense?: boolean;
}

/** One event row — top-level or, indented and dense, an import's child (design §6.4). Opens the
 * detail drawer on click, Enter or Space; the entity link handles its own Enter/click (CA21). */
const EventRow = ({ event, onOpen, dense }: EventRowProps) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AppContext);
  const link = entityLink(event, (permission) => hasUserPermission(currentUser, permission));

  return (
    <div
      role="row"
      tabIndex={0}
      onClick={(e) => onOpen(event, e.currentTarget as HTMLElement)}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        // The entity link owns its own Enter — checked BEFORE preventDefault, or the anchor's
        // native activation never happens even though this handler then bails out (CA21 bug).
        if ((e.target as HTMLElement).closest('a')) return;
        e.preventDefault();
        onOpen(event, e.currentTarget as HTMLElement);
      }}
      className={cn(
        'group grid cursor-pointer items-center gap-3 border-b px-4 outline-none hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-inset',
        GRID_COLS,
        dense && 'pl-8'
      )}
      style={{ height: dense ? 44 : 52 }}
    >
      <span role="cell" className={`font-mono text-[12px] ${MUTED_CELL}`}>
        {dense ? formatTime(event.occurredAt) : `${numericDate(dayKey(event.occurredAt))} ${formatTime(event.occurredAt)}`}
      </span>
      <ActorCell event={event} />
      <span role="cell" className={`truncate text-[13px] ${MUTED_CELL}`}>{moduleLabel(t, event.module)}</span>
      <div role="cell" className="min-w-0">
        {link ? (
          <Link
            to={link}
            onClick={(e) => e.stopPropagation()}
            className="inline-block max-w-full truncate align-top text-[13px] font-medium underline hover:no-underline"
          >
            {event.entityLabel ?? event.entityId}
          </Link>
        ) : (
          <span className="inline-block max-w-full truncate align-top text-[13px] font-medium">{event.entityLabel ?? event.entityId}</span>
        )}
        <p className={`truncate font-mono text-[11.5px] ${MUTED_CELL}`}>
          {event.entityType} · {event.entityId}
        </p>
      </div>
      <div role="cell">
        <ActionPill action={event.action} />
      </div>
      <span role="cell" className={`truncate text-[13px] ${MUTED_CELL}`}>{summary(t, event)}</span>
    </div>
  );
};

export default EventRow;
